import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
} from '@mui/material';
import {
  Dashboard,
  Settings,
  Add,
  Edit,
  Storage,
  Build,
  People,
  Business,
  PersonAdd,
  TrendingUp,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageHeader from '../components/ui/PageHeader';

const AdminPage: React.FC = () => {
  const navigate = useNavigate();

  const adminSections = [
    {
      title: 'Gestione Macchine',
      description: 'Aggiungi, modifica e configura le macchine laser',
      icon: <Settings />,
      items: [
        { title: 'Inserisci Nuova Macchina', icon: <Add />, path: '/admin/machines/add' },
        { title: 'Gestione Macchine', icon: <Edit />, path: '/machines/manage' },
      ]
    },
    {
      title: 'Gestione Utenti',
      description: 'Gestione reseller e clienti',
      icon: <People />,
      items: [
        { title: 'gestione Reseller', icon: <Business />, path: '/admin/resellers' },
        { title: 'Gestione Clienti', icon: <PersonAdd />, path: '/admin/customers' },
      ]
    },
    {
      title: 'Configurazione Sistema',
      description: 'Impostazioni generali del sistema',
      icon: <Storage />,
      items: [
        { title: 'Database', icon: <Storage />, path: '/admin/database' },
        { title: 'RustDesk / Assistenza Remota', icon: <Settings />, path: '/remote-assistance' },
        { title: 'Backup', icon: <TrendingUp />, path: '/admin/backup' },
      ]
    },
    {
      title: 'Manutenzione',
      description: 'Gestione manutenzioni e diagnosi',
      icon: <Build />,
      items: [
        { title: 'Log Manutenzioni', icon: <Edit />, path: '/admin/maintenance' },
      ]
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <PageHeader
          title="🛠️ Pannello Amministrazione"
          subtitle="Gestione completa del sistema HMI Laser Dashboard"
        />
      </motion.div>

      {/* Admin Sections */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {adminSections.map((section, index) => (
          <motion.div
            key={section.title}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 * index, duration: 0.5 }}
          >
            <Paper 
              sx={{ 
                p: 3, 
                borderRadius: 2,
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 32px rgba(15, 23, 42, 0.08)',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: 'primary.main', 
                  color: 'white',
                  mr: 2
                }}>
                  {section.icon}
                </Box>
                <Box>
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                    {section.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {section.description}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Box>
                {section.items.map((item) => (
                  <Box
                    key={item.title}
                    onClick={() => navigate(item.path)}
                    sx={{ 
                      borderRadius: 1,
                      mb: 1,
                      p: 1,
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      }
                    }}
                  >
                    <Box sx={{ mr: 2, color: 'primary.main' }}>
                      {item.icon}
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {item.title}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </motion.div>
        ))}
      </Box>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
      >
        <Paper sx={{ p: 3, mt: 4, textAlign: 'center', background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <Typography variant="h6" gutterBottom>
            🚀 Azioni Rapide
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/admin/machines/add')}
              sx={{ minWidth: 150 }}
            >
              Nuova Macchina
            </Button>
            <Button
              variant="contained"
              startIcon={<PersonAdd />}
              onClick={() => navigate('/admin/resellers')}
              sx={{ minWidth: 150 }}
            >
              Nuovo Reseller
            </Button>
            <Button
              variant="outlined"
              startIcon={<Dashboard />}
              onClick={() => navigate('/dashboard')}
              sx={{ minWidth: 150 }}
            >
              Torna Dashboard
            </Button>
          </Box>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default AdminPage;
